import { defineStore } from 'pinia'
import type { User } from 'oidc-client-ts'
import { userManager, buildCognitoLogoutUrl } from '@/auth/oidcConfig'

interface AuthState {
  user: User | null
  isInitialized: boolean
  /**
   * ログインに失敗したときの表示用(App.vue がエラー画面を出す)。
   * noAccess = Cognito のトークン生成前トリガーで拒否された(審査Webのグループに入っていない)。
   */
  authError: { kind: 'noAccess' | 'error'; message: string } | null
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    isInitialized: false,
    authError: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.user && !state.user.expired,
    accessToken: (state) => state.user?.id_token ?? null, // API GatewayのJWT AuthorizerはIDトークンのaudクレームを検証する設計
    displayName: (state) =>
      (state.user?.profile?.email as string | undefined) ??
      (state.user?.profile?.sub as string | undefined) ??
      '不明なユーザー',
    /**
     * メモ欄の「@」補完で挿入する名前。
     * Cognitoのpreferred_usernameを使う。IDトークンに含まれていない場合
     * (アプリクライアントの「読み取り可能な属性」未設定など)はemailにフォールバックする。
     */
    memberName: (state) =>
      (state.user?.profile?.preferred_username as string | undefined) ??
      (state.user?.profile?.email as string | undefined) ??
      null,

    /**
     * ユーザ管理メニューの表示可否に使う。IDトークンの cognito:groups クレームに
     * "Admin" が含まれるかどうかで判定する(Admin/Generalの2グループのみ運用)。
     * oidc-client-ts はJWTのクレームをそのままprofileへ展開するため、配列として
     * 入っているのが通常だが、環境差を考慮してstring/arrayの両方に対応する。
     */
    isAdmin: (state) => {
      const groups = state.user?.profile?.['cognito:groups'] as string[] | string | undefined
      if (!groups) return false
      if (Array.isArray(groups)) return groups.includes('Admin')
      return groups.split(',').map((g) => g.trim()).includes('Admin')
    },
  },

  actions: {
    /** アプリ起動時に呼ぶ。既存セッション/リダイレクト後のコールバックを処理する */
    async initialize() {
      // Cognitoからのコールバック(?code=...)を処理中の場合
      // Cognito からエラーで戻ってきた場合(?error=...)。自動でログイン画面へ戻すと無限ループになるため止める
      const params = new URLSearchParams(window.location.search)
      if (window.location.pathname === '/callback' && params.get('error')) {
        this.failLogin(params.get('error_description') || params.get('error'))
        return
      }
      if (window.location.pathname === '/callback' && window.location.search.includes('code=')) {
        let user: User | null = null
        try {
          user = await userManager.signinCallback() ?? null
        } catch (e) {
          // 【市民革命とのユーザープール共有 2026-10】
          // 審査Webのグループ(Admin/General)に入っていないユーザーは、トークン発行時(コードの交換時)に
          // Cognito のトークン生成前トリガーで拒否される。以前はこの例外を受け止めておらず、
          // 初期化が終わらないまま「読み込み中…」で止まっていた。
          this.failLogin(e)
          return
        }
        this.user = user
        // login() でCognitoへ渡しておいた「認証前に開こうとしていたURL」に戻す。
        // これが無いと、審査WebのURL(例: /judge?asin=B0XXXX)を直接開いた場合に
        // 認証後は必ずトップ画面へ飛ばされてしまう。
        const returnTo = typeof user?.state === 'string' && user.state.startsWith('/') ? user.state : '/'
        window.history.replaceState({}, document.title, returnTo)
      } else {
        this.user = await userManager.getUser()
      }
      this.isInitialized = true

      userManager.events.addUserLoaded((user) => {
        this.user = user
      })
      userManager.events.addUserUnloaded(() => {
        this.user = null
      })
      userManager.events.addSilentRenewError((err) => {
        console.error('トークンの自動更新に失敗しました', err)
      })

      // 未認証の場合、ログインボタンを待たずCognitoの認証画面へ自動遷移する。
      // (コールバック処理直後にログインが確立した場合はこの分岐に来ない)
      if (!this.isAuthenticated) {
        await this.login()
      }
    },

    async login() {
      // 認証前に開こうとしていたURL(パス+クエリ)を控えて、認証後に戻れるようにする。
      // /callback 自体を控えてしまうとループするため除外する。
      const current = window.location.pathname + window.location.search
      const returnTo = window.location.pathname === '/callback' ? '/' : current

      // Managed Loginの日本語化。lang=ja を付けて /oauth2/authorize を呼ぶと、
      // 以降はブラウザにCookieとして言語設定が保存される(Cognito公式の仕組み)。
      await userManager.signinRedirect({
        state: returnTo,
        extraQueryParams: { lang: 'ja' },
      })
    },

    /** ログイン失敗を記録し、エラー画面を出す(自動でログイン画面へは戻さない) */
    failLogin(e: unknown) {
      const err = e as { message?: string; error_description?: string } | string | null
      const text = typeof err === 'string' ? err : `${err?.message ?? ''} ${err?.error_description ?? ''}`
      console.error('ログインに失敗しました', e)
      window.history.replaceState({}, document.title, '/')
      this.user = null
      this.authError = text.includes('APP_ACCESS_DENIED')
        ? { kind: 'noAccess', message: 'このアカウントには、セルモン商品審査の利用権限がありません。' }
        : { kind: 'error', message: `ログインできませんでした。${text.trim() ? `（${text.trim()}）` : ''}` }
      this.isInitialized = true
    },

    /** エラー画面の「別のアカウントでログイン」: Cognito 側のログイン状態も消してからログイン画面へ */
    async switchAccount() {
      await userManager.removeUser()
      window.location.href = buildCognitoLogoutUrl()
    },

    async logout() {
      await userManager.removeUser()
      window.location.href = buildCognitoLogoutUrl()
    },
  },
})
